import { OptionallyVisible } from 'components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import merchantsApi from 'services/merchants';
import { BridgeFiatRail, FiatPayoutSettingsPayload, MerchantPayoutFlow, PayoutDestinationType } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import IconAdd from 'components/icons/add';
import { Select, SelectItem } from 'components/inputs/select/select';
import { Buttons } from 'components/modal/styles';

import { ReduxToolkitError } from 'types/http';

import { PayoutDestinationsDataRow } from '../../../types';
import { usePayout } from '../context';
import { usePayoutSetupStep } from '../context/modal';
import { Delimiter, Label, Section } from '../styles';
import { PayoutType } from '../types';
import PayoutBankDetails from './details';
import LABELS, { KEYS } from './keys';
import { AddNewItemSelectItem, ErrorText } from './styles';

interface PayoutDestinationsSelectionProps {
  payoutDestinations: PayoutDestinationsDataRow[];
}

const BankAccountSelection = ({ payoutDestinations }: PayoutDestinationsSelectionProps) => {
  const { t } = useTranslation();
  const [selectedBankAccount, setSelectedBankAccount] = useState<SelectItem | null>(null);
  const payoutStep = usePayoutSetupStep();
  const payout = usePayout();
  const [payoutSettingsMutation, { isLoading, error }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const canProceed = Boolean(selectedBankAccount);
  const backButtonLabel = payoutStep.canGoBack() ? t(LABELS.ACTIONS.BACK) : t(LABELS.ACTIONS.CANCEL);

  const bankAccountSelectItems: SelectItem[] = payoutDestinations
    .filter((destination) => destination.type === PayoutDestinationType.Bank)
    .map((destination) => {
      const accountNumberForPaymentRail = {
        [BridgeFiatRail.ACH]: destination.account,
        [BridgeFiatRail.ACH_SAME_DATE]: destination.account,
        [BridgeFiatRail.WIRE]: destination.account,
        [BridgeFiatRail.SEPA]: destination.account,
        [BridgeFiatRail.SWIFT]: destination.account,
        [BridgeFiatRail.SPEI]: destination.account,
        [BridgeFiatRail.PIX]: destination.account,
        [BridgeFiatRail.BRE_B]: destination.account,
      }[payout[PayoutType.Fiat].paymentRail ?? BridgeFiatRail.ACH];

      return {
        key: destination.uuid,
        label: `${destination.name} •••• ${accountNumberForPaymentRail.replaceAll('*', '')}`,
      };
    });

  const bankAccountSelectItemsWithNewItem: SelectItem[] = [
    {
      key: KEYS.NEW_ITEM,
      label: (
        <AddNewItemSelectItem>
          <IconAdd /> {t(LABELS.NEW_BANK_ACCOUNT_LABEL)}
        </AddNewItemSelectItem>
      ) as any, // I dont want to play with Select types, it works just fine
    },
    ...bankAccountSelectItems,
  ];

  const onBankSelect = (item: SelectItem) => {
    setSelectedBankAccount(item);
  };

  const onProceedWithExistingAccount = async () => {
    if (!selectedBankAccount) {
      return;
    }

    const noNeedToUpdatePaymentRail = payout.merchant.payoutFlow === MerchantPayoutFlow.DIRECT;

    const payload: FiatPayoutSettingsPayload = {
      merchantId: payout.merchant.uuid,
      payoutDestinationUuid: selectedBankAccount.key,
      payoutMemo: '',
      ...(!noNeedToUpdatePaymentRail && { paymentRail: payout[PayoutType.Fiat].paymentRail }),
    };

    const response = await payoutSettingsMutation(payload);

    if ('error' in response) {
      return;
    }

    payout.setPayoutState({
      ...payout,
      [PayoutType.Fiat]: {
        ...payout[PayoutType.Fiat],
        bankAccountUuid: selectedBankAccount?.key,
        paymentRail: payout[PayoutType.Fiat].paymentRail ?? BridgeFiatRail.ACH,
      },
    });

    payoutStep.proceed();
  };

  return (
    <>
      <Section>
        <Label>{t(LABELS.ACCOUNT_SELECTION_LABEL, { currency: payout.merchant.mainFiatCurrency })}</Label>
        <Select<SelectItem, false>
          fullWidth
          value={selectedBankAccount ?? undefined}
          items={bankAccountSelectItemsWithNewItem}
          onChange={onBankSelect}
          getItemLabel={(item) => item?.label ?? item?.key ?? ''}
          multiple={false}
          closeDropdownOnSelect
          placeholder={t(LABELS.ACCOUNT_SELECTION_PLACEHOLDER)}
        />
      </Section>
      <OptionallyVisible visible={selectedBankAccount?.key === KEYS.NEW_ITEM}>
        <>
          <Delimiter />
          <PayoutBankDetails />
        </>
      </OptionallyVisible>
      <OptionallyVisible visible={Boolean(selectedBankAccount?.key !== KEYS.NEW_ITEM)}>
        <OptionallyVisible visible={Boolean((error as ReduxToolkitError)?.data?.errorMessage)}>
          <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage}</ErrorText>
        </OptionallyVisible>
        <Delimiter />
        <Buttons>
          <Button secondary onClick={payoutStep.goBack}>
            {backButtonLabel}
          </Button>
          <ButtonWithLoading isLoading={isLoading} type="submit" disabled={!canProceed} primary onClick={onProceedWithExistingAccount}>
            {t(LABELS.ACTIONS.CONTINUE)}
          </ButtonWithLoading>
        </Buttons>
      </OptionallyVisible>
    </>
  );
};

export default BankAccountSelection;
